import {
  AccessibleContent,
  Alert,
  Button,
  Flex,
  Heading,
  IconAddLine,
  InlineList,
  Modal,
  Pill,
  ScreenReaderContent,
  Spinner,
  Tag,
  Text,
  TextInput,
  ToggleDetails,
} from "@instructure/ui";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Files } from "../../../../../services/FilesService";
import { QualitativeMeasureService } from "../../../../../services/QualitativeMeasureService";
import { StructuredQuestionService } from "../../../../../services/StructuredQuestionService";
import { institutionalEffectivenessEnabled } from "../../../../../utils/constants";
import { TermsContext } from "../../../../../utils/Context";
import { fileWriter } from "../../../../../utils/FileUtils";
import { ApDataContext } from "../../../../../views/Plan";
import { Card } from "../../../../Card";
import {
  ERROR,
  useNotification,
} from "../../../../CustomHooks/NotificationProvider";
import { rteHasContent } from "../../../../RichTextArea/RichTextEditor";
import RichTextViewer from "../../../../RichTextArea/RichTextViewer";
import { GoalsSingleSelect } from "../../../../Selectors/GoalSelect";
import { ObjectiveSelect } from "../../../../Selectors/ObjectiveSelect";
import { TermSelect } from "../../../../TermManagement/Terms/TermSelect";
import { ON_OR_BEFORE, validateDueTerm } from "../../dueDateUtils";
import { QUALITATIVE, emailVerification } from "../../utils";

const QualMeasureTemplateCard = ({ templateFields }) => {
  const {
    sectionIndex,
    measureIndex,
    selectedCycle,
    handleCreateMeasure,
    sectionDueTermId,
    includeGoalsObjs,
  } = useContext(ApDataContext);
  const { t } = useTranslation();
  const { insightsTerms } = useContext(TermsContext);
  const showNotification = useNotification();
  const [name, setName] = useState();
  const [uploadEvidence, setUploadEvidence] = useState();
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [selectedObjective, setSelectedObjective] = useState(null);
  const [triggerObjectives, setTriggerObjectives] = useState(false);
  const [email, setEmail] = useState("");
  const [invalidEmail, setInvalidEmail] = useState(false);
  const [duplicateEmail, setDuplicateEmail] = useState(false);
  const [reviewers, setReviewers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [selectedDueTerm, setSelectedDueTerm] = useState(null);
  const [dueTermError, setDueTermError] = useState(null);

  const handleDueTermSelect = (termId) => {
    setSelectedDueTerm(termId);
    if (termId && sectionDueTermId) {
      const error = validateDueTerm(
        termId,
        sectionDueTermId,
        insightsTerms,
        ON_OR_BEFORE
      );
      setDueTermError(error);
    } else {
      setDueTermError(null);
    }
  };

  useEffect(() => {
    setName(templateFields.name);
    setUploadEvidence(templateFields.upload_evidence ? "yes" : "no");
  }, [templateFields]);

  const handleAddAssignee = () => {
    if (!emailVerification(email)) {
      setInvalidEmail(true);
      setDuplicateEmail(false);
    } else if (reviewers.includes(email)) {
      setDuplicateEmail(true);
      setInvalidEmail(false);
    } else {
      setInvalidEmail(false);
      setDuplicateEmail(false);
      setReviewers((prevReviewers) => [...prevReviewers, email]);
      setEmail("");
    }
  };

  const handleGoalSelect = (goalId) => {
    setSelectedGoal(goalId);
    setSelectedObjective(null);
    setTriggerObjectives(true);
  };

  const handleObjectiveSelect = (objectiveId) => {
    setSelectedObjective(objectiveId);
  };

  const missingRequiredQualitativeFields = () => {
    const missing_fields = [];
    if (includeGoalsObjs && !selectedGoal) {
      missing_fields.push(t("cyclesAndGoals.goal"));
    }
    if (reviewers.length === 0) {
      missing_fields.push(t("assessment.email"));
    }
    if (missing_fields.length === 0) {
      return false;
    }
    let alert_message = "";
    const num_missing_fields = missing_fields.length;
    if (num_missing_fields === 1) {
      alert_message +=
        missing_fields[0] +
        t("notifications.assessments.missingMultipleFieldsErrorMessage");
    } else {
      missing_fields.forEach((field, idx) => {
        if (idx === num_missing_fields - 1) {
          alert_message +=
            t("assessment.and") +
            " " +
            field +
            t("notifications.assessments.missingSingleFieldErrorMessage");
        } else if (num_missing_fields === 2) {
          alert_message += field + " ";
        } else {
          alert_message += field + ", ";
        }
      });
    }
    showNotification(alert_message, ERROR);
    return true;
  };

  const handleQualitativeVerification = async () => {
    if (missingRequiredQualitativeFields()) return;

    setSaving(true);
    try {
      const goal = selectedCycle.goals.find((g) => g.id === selectedGoal);
      const objective = goal?.objectives?.find(
        (o) => o.id === selectedObjective
      );

      const noObjective = !selectedObjective || selectedObjective === "0";

      const measureAdd = {
        name: name,
        upload_evidence: uploadEvidence,
        goal: goal
          ? { id: selectedGoal, description: goal?.description ?? "" }
          : null,
        objective: noObjective
          ? null
          : {
              id: selectedObjective,
              description: objective?.description ?? "",
            },
        assignees: [...reviewers].sort().join(","),
        sequence: measureIndex,
        insights_term_id: selectedDueTerm ?? null,
      };
      const measure = {
        planId: null,
        sectionId: null,
        qualitativeMeasure: measureAdd,
      };

      let newMeasure = await QualitativeMeasureService.createMeasure(measure);

      if (templateFields.description) {
        const instructionsFile = fileWriter(
          templateFields.description,
          "response.html",
          "text/html"
        );
        await Files.saveFile(
          instructionsFile,
          "qualitativeMeasure",
          newMeasure.ddl_id
        );
        newMeasure = {
          ...newMeasure,
          type: QUALITATIVE,
          instructions: templateFields.description,
        };
      } else {
        newMeasure = { ...newMeasure, type: QUALITATIVE };
      }

      // Copy structured questions from template to the new measure
      if (
        institutionalEffectivenessEnabled &&
        templateFields.structured_questions &&
        templateFields.structured_questions.length > 0
      ) {
        await StructuredQuestionService.upsertStructuredQuestions(
          newMeasure.ddl_id,
          templateFields.structured_questions
        );
      }

      handleCreateMeasure(newMeasure, sectionIndex, measureIndex);
    } catch {
      showNotification(
        t("notifications.assessments.createAssessmentError"),
        ERROR
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card style={{ backgroundColor: "#FFFFFF", padding: "0" }}>
      <Flex wrap="wrap" padding="small">
        <Flex.Item size="100%">
          <Pill color="info" themeOverride={{ maxWidth: "30rem" }}>
            {t("assessment.qualitativeMeasure")}
          </Pill>
        </Flex.Item>
        <Flex.Item size="100%" margin="x-small 0 0 0">
          <Text as="div" size="small" lineHeight="double">
            <Text weight="bold" size="small">
              {t("global.name")}:{" "}
            </Text>
            {templateFields.name}
          </Text>
        </Flex.Item>
        <Flex.Item>
          <Text as="div" size="small" lineHeight="double">
            <Text weight="bold" size="small">
              {t("measures.uploadEvidence")}?:{" "}
            </Text>
            {templateFields.upload_evidence
              ? t("dialogYesNo.yes")
              : t("dialogYesNo.no")}
          </Text>
        </Flex.Item>
        {rteHasContent(templateFields.description) && (
          <Flex.Item size="100%" margin="xxx-small 0 0 0">
            <ToggleDetails
              summary={
                <Text weight="bold" size="small">
                  {t("assessment.instructions")}
                </Text>
              }
            >
              <RichTextViewer
                value={templateFields.description}
                ariaLabel={t("assessment.instructions")}
              />
            </ToggleDetails>
          </Flex.Item>
        )}
        {institutionalEffectivenessEnabled && (
          <Flex.Item size="100%" margin="xxx-small 0 small 0">
            <ToggleDetails
              summary={
                <Text weight="bold" size="small">
                  {t("structuredQuestions.structuredQuestions")}
                </Text>
              }
            >
              {templateFields?.structured_questions?.length > 0 ? (
                templateFields.structured_questions.map((q, i) => (
                  <Text as="div" size="small" key={q.id || i}>
                    {i + 1}. {q.question_text}
                  </Text>
                ))
              ) : (
                <Text size="small" fontStyle="italic">
                  {t("structuredQuestions.noQuestions")}
                </Text>
              )}
            </ToggleDetails>
          </Flex.Item>
        )}
        {includeGoalsObjs && (
          <Flex.Item size="100%">
            <Heading as="h3" level="h5" border="bottom">
              {t("assessment.with")}
            </Heading>
            <Flex gap="small" wrap="wrap" margin="small 0">
              <Flex.Item shouldGrow shouldShrink>
                <GoalsSingleSelect
                  id={`goal-select-section-${sectionIndex}-measure-${measureIndex}`}
                  selectHandler={handleGoalSelect}
                  goalsList={selectedCycle.goals}
                  selectedGoal={selectedGoal}
                  size="small"
                  isRequired
                />
              </Flex.Item>
              <Flex.Item shouldGrow shouldShrink>
                <ObjectiveSelect
                  id={`objective-select-section-${sectionIndex}-measure-${measureIndex}`}
                  selectedObjective={selectedObjective}
                  selectedGoal={selectedGoal}
                  selectedCycle={selectedCycle}
                  selectHandler={handleObjectiveSelect}
                  trigger={triggerObjectives}
                  setTrigger={setTriggerObjectives}
                  size="small"
                />
              </Flex.Item>
            </Flex>
          </Flex.Item>
        )}
        <Flex.Item size="100%">
          <Heading as="h3" level="h5" border="bottom" margin="0 0 small 0">
            <ScreenReaderContent>
              {t("measures.assignedTo")}
            </ScreenReaderContent>
          </Heading>
          <Flex gap="small" alignItems="start">
            <Flex.Item shouldGrow shouldShrink>
              <TextInput
                type="email"
                renderLabel={
                  <Text size="small">{t("measures.assignedTo")}</Text>
                }
                placeholder={t("assessment.email")}
                size="small"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                messages={
                  invalidEmail
                    ? [{ text: t("assessment.invalidEmail"), type: "error" }]
                    : duplicateEmail
                      ? [
                          {
                            text: t("assessment.emailAlreadyAdded"),
                            type: "error",
                          },
                        ]
                      : []
                }
              />
            </Flex.Item>
            <Flex.Item
              align={invalidEmail || duplicateEmail ? "center" : "end"}
            >
              <Button
                size="small"
                renderIcon={IconAddLine}
                onClick={handleAddAssignee}
              >
                {t("global.add")}
              </Button>
            </Flex.Item>
            <Flex.Item size="30%">
              <TermSelect
                isSingle
                id={`due-term-select-${sectionIndex}-${measureIndex}`}
                label={t("measures.dueTerm")}
                selectHandler={handleDueTermSelect}
                selectedTerms={selectedDueTerm}
                noPastTerms
                noDefaultTerm
                allowNone
                size="small"
              />
            </Flex.Item>
          </Flex>
          {dueTermError && (
            <Alert variant="error" margin="small 0 0 0">
              {dueTermError}
            </Alert>
          )}
        </Flex.Item>
        {reviewers.length > 0 && (
          <Flex.Item size="100%">
            <InlineList>
              {reviewers.map((email, key) => (
                <InlineList.Item key={key} margin="small 0 0 0">
                  <Tag
                    text={
                      <AccessibleContent alt={t("assessment.deleteAssessor")}>
                        {email}
                      </AccessibleContent>
                    }
                    dismissible
                    onClick={() => {
                      setReviewers((prevReviewers) =>
                        prevReviewers.filter((reviewer) => reviewer !== email)
                      );
                    }}
                  />
                </InlineList.Item>
              ))}
            </InlineList>
          </Flex.Item>
        )}
      </Flex>
      <Modal.Footer>
        <Button
          color="primary"
          size="small"
          onClick={handleQualitativeVerification}
          disabled={saving || !!dueTermError}
        >
          {saving ? (
            <Spinner renderTitle={t("global.pleaseWait")} size="x-small" />
          ) : (
            t("global.save")
          )}
        </Button>
      </Modal.Footer>
    </Card>
  );
};

export default QualMeasureTemplateCard;
